<template>
  <div :class="$style.container">
    <div :class="$style.header">
      <div :class="[$style.row, $style.heading]">
        <slot name="woc-heading">
          <div :class="$style.slotHeading">
            <span :class="$style.title">{{ title }}</span>
            <span :class="$style.productCount">{{ productCountLabel }}</span>
          </div>
        </slot>
      </div>
      <div v-if="sortedSkus.length > 0" :class="[$style.row, $style.sort]">
        <div :class="$style.sortBy">{{ sortByLabel }}</div>
        <NsSlottedDropdown
          :class="$style.sortDropdown"
          :selected="sortByValue"
          :placeholder="selectPlaceholder"
          @select="sortSelect"
        >
          <div v-for="opt in sortOptions" :key="opt.id" :data-value="opt.id">
            <span>{{ opt.title }}</span>
          </div>
        </NsSlottedDropdown>
      </div>
    </div>
    <NsProductList
      :items="sortedSkus"
      :skus-per-load="skusPerLoad"
      @product-update="handleProductUpdate"
    />
  </div>
</template>

<script>
import { UserService } from "@nuskin/ns-account";
import { ShopContextService } from "@nuskin/ns-shop";
import { events } from "@nuskin/ns-util";
import { NsSlottedDropdown } from "@nuskin/design-components";
import debounce from "lodash/debounce";

import NsProductList from "./NsProductList.vue";
import { fromJsonString } from "../utils/helpers";

const PRODUCT_COUNT = "%count%";
const DISTRIBUTOR_ONLY_SORT_OPTIONS = ["pointHighToLow", "pointLowToHigh"];

/**
 * NsProductListSortable
 * Events
 * @event sort-by-select  Fired when a dropdown item is selected with (id) => {}
 */

export default {
  name: "NsProductListSortable",
  components: { NsSlottedDropdown, NsProductList },
  props: {
    /**
     * 'Products' label for translations. Should include '%count%'. See default
     * for English example
     */
    productsLabel: {
      type: String,
      default: "%count% Products"
    },
    /**
     * The placehoder text in the sortBy dropdown
     */
    selectPlaceholder: {
      type: String,
      default: ""
    },
    /**
     * Array of skus for product list. Takes array of strings or objects
     * ([sku] || [{ sku }])
     */
    skus: {
      type: [String, Array],
      default: () => []
    },
    /**
     * Object containing the search scores for sorting
     */
    skuSearchScores: {
      type: Object,
      default: null
    },
    /**
     * SortBy label
     */
    sortByLabel: {
      type: String,
      default: ""
    },
    /**
     * SortBy options. Array of objects:  [{ id, title, sort, order }]
     */
    sortByOptions: {
      type: Array,
      default: () => []
    },
    /**
     * Set sortBy selected option programmically. Should match one of
     * sortByOptions[n].id
     */
    sortBySelected: {
      type: String,
      default: ""
    },
    /**
     * Title of list
     */
    title: {
      type: String,
      default: ""
    },
    /**
     * Initial number of products displayed and number added per "Load More" button click. Set to 0 to disable.
     */
    skusPerLoad: {
      type: Number,
      default: 20
    }
  },
  data: function() {
    return {
      products: {},
      sortedSkus: fromJsonString(this.skus, []),
      sortByValue: this.sortBySelected
    };
  },
  computed: {
    localSkus() {
      return fromJsonString(this.skus, []);
    },
    productCountLabel() {
      const productCount = this.sortedSkus.length;
      return this.productsLabel.replace(PRODUCT_COUNT, productCount);
    },
    sortOptions() {
      return this.sortByOptions.filter(option => {
        // exclude distributor_only filter option if user != distributor
        if (DISTRIBUTOR_ONLY_SORT_OPTIONS.includes(option.id)) {
          const user = UserService.getUser();
          if (!UserService.isLoggedIn() || !user.isDistributor()) {
            return false;
          }
        }
        // exclude sorting by best match unless scores provided
        return option.id !== "bestMatch" || !!this.skuSearchScores;
      });
    }
  },
  watch: {
    products() {
      // each product fires an update, so debouncing the sortBy will call it
      // only once per batch of updates
      this.debouncedSortBy();
    },
    localSkus: {
      handler: "sortBy"
    },
    sortBySelected(value) {
      this.sortByValue = value;
    },
    sortByValue: {
      handler: "sortBy"
    }
  },
  methods: {
    debouncedSortBy: debounce(function() {
      this.sortBy("debounced");
    }, 200),
    /**
     * Saves the products' data in local state, so it can sort by this data
     */
    handleProductUpdate(product) {
      if (product) {
        this.products = {
          ...this.products,
          [product.sku]: product
        };
      }
    },

    /**
     * See if the SKU is a base SKU.
     */
    isBase(product) {
      return product.sku.substring(2, 4) === "55";
    },

    /**
     * Used to get the correct price based on your login price type
     * @param productObject
     * @returns {number}
     * @private
     */
    getPricingBasedOnLogin(productObject) {
      let priceType = "WRTL";
      if (this.isBase(productObject)) {
        priceType = "min-WRTL";
      }
      let productPrice = 0;
      if (UserService.isLoggedIn()) {
        priceType = ShopContextService.getPriceType(
          false,
          UserService.getUser()
        );
      }
      if (productObject) {
        // eslint-disable-next-line no-prototype-builtins
        if (productObject.hasOwnProperty("priceMap")) {
          Object.keys(productObject.priceMap).forEach(itemPriceType => {
            if (itemPriceType === priceType) {
              productPrice = productObject.priceMap[itemPriceType];
            }
          });
        }
      }

      return productPrice;
    },

    /**
     * Used to get the correct PSV value based on your login price type
     * @param productObject
     * @returns {number}
     * @private
     */
    getPSVBasedOnLogin(productObject) {
      let priceType = "WRTL";
      if (this.isBase(productObject)) {
        priceType = "min-WRTL";
      }
      let productPSV = 0;
      if (UserService.isLoggedIn()) {
        priceType = ShopContextService.getPriceType(
          false,
          UserService.getUser()
        );
      }
      if (productObject) {
        // eslint-disable-next-line no-prototype-builtins
        if (productObject.hasOwnProperty("pvMap")) {
          Object.keys(productObject.pvMap).forEach(pvPriceType => {
            if (pvPriceType === priceType) {
              productPSV = productObject.pvMap[pvPriceType];
            }
          });
        }
      }

      return productPSV;
    },
    sortSelect(value) {
      this.sortByValue = value;
      this.$emit("sort-by-select", value);
      events.publish(events.shop.SEARCH_SORT_DROPDOWN);
    },
    sortBy() {
      // Find sortOption
      const sortOption = this.sortOptions.find(
        option => option.id === this.sortByValue
      );

      // if option doesn't exist do nothing.
      if (!sortOption) return;

      const sort = sortOption.sort;
      const order = sortOption.order;

      // if default or no sort, reset to this.skus
      if (sort === "default" || !sort) {
        this.sortedSkus = this.localSkus;
        return;
      }

      this.sortedSkus = [...this.localSkus].sort((x, y) => {
        // get full product objects
        const a = this.products[typeof x === "object" ? x.sku : x] || {};
        const b = this.products[typeof y === "object" ? y.sku : y] || {};

        if (sort === "best") {
          a.searchScore = this.skuSearchScores[a.sku] || 0;
          b.searchScore = this.skuSearchScores[b.sku] || 0;
        }

        if (sort === "title") {
          return order === "desc"
            ? (b[sort] || "").localeCompare(a[sort] || "")
            : (a[sort] || "").localeCompare(b[sort] || "");
        } else if (sort === "price") {
          return order === "desc"
            ? this.getPricingBasedOnLogin(a) - this.getPricingBasedOnLogin(b)
            : this.getPricingBasedOnLogin(b) - this.getPricingBasedOnLogin(a);
        } else if (sort === "pv") {
          return order === "desc"
            ? this.getPSVBasedOnLogin(a) - this.getPSVBasedOnLogin(b)
            : this.getPSVBasedOnLogin(b) - this.getPSVBasedOnLogin(a);
        } else if (sort === "best") {
          return b["searchScore"] - a["searchScore"];
        } else {
          return order === "desc"
            ? sortValue(a[sort]) - sortValue(b[sort])
            : sortValue(b[sort]) - sortValue(a[sort]);
        }
      });

      function sortValue(item) {
        if (typeof item === "undefined" || item === null) {
          return 0;
        }

        return item;
      }
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/colors";

.container {
  margin: 0 auto;
  padding: 0 0 15px;
  max-width: 1335px;
  background-color: rgb(246, 247, 247);
  box-sizing: border-box;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  background-color: rgb(246, 247, 247);
  padding: 10px 15px;
  padding-bottom: 0px;
}

.row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  &.heading {
    margin: 5px 0 15px;
  }

  &.sort {
    justify-content: flex-end;
    align-items: center;
    flex-grow: 1;
    margin-left: 40px;
  }
}

.slotHeading {
  display: flex;
  align-items: baseline;
  line-height: normal;
}

.title {
  display: none;
  color: rgb(39, 37, 37);
  font-size: 22px;
  font-weight: 600;
}

.productCount {
  display: none;
  color: rgb(99, 102, 106);
  font-size: 14px;
  font-weight: normal;
}

.sortBy {
  margin-right: 10px;
  color: rgb(99, 102, 106);
  font-size: 14px;
  font-weight: normal;
  line-height: normal;
}

.sort-selector {
  padding: 7px 10px;
  border: 1px solid rgb(209, 209, 209);
  border-radius: 2px;
  width: 160px;
  background: #ffffff;
  font-size: 16px;
  line-height: normal;
  text-align: left;
}

.sortDropdown {
  width: 210px;
}

@media (min-width: 768px) {
  .container {
    padding: 25px 15px;
  }

  .header {
    flex-wrap: nowrap;
    padding: 0;
  }

  .slotHeading {
    margin-left: 15px;
  }

  .title {
    display: block;
  }

  .productCount {
    display: block;
    margin-left: 10px;
  }

  .row {
    justify-content: flex-start;

    &.heading {
      margin-bottom: 20px;
    }

    &.sort {
      flex-grow: 0;
      margin: 0;
      margin-bottom: 15px;
    }
  }

  .sortBy {
    color: rgb(187, 188, 188);
    font-size: 16px;
  }

  .sort-selector {
    width: 210px;
  }
}
</style>
